import { useMemo } from 'react';
import { useFormik, useFormikContext } from 'formik';
import { PlusOutlined } from '@ant-design/icons';
import { Row, Space, Typography } from 'antd';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import FormSelect from 'components/new-ui/composed/formSelect/FormSelect';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import Notification from 'components/ui/base/notification/Notification';
import { InvoiceConfigBankAccount } from 'store/invoice-config/types';
import { PaymentMethodDraft, PaymentMethodsFormFields } from '../../validationSchemas';
import { InvoiceTemplateFormFields, PaymentMethods } from './types';
import { paymentMethodSchema } from './validationSchema';

type Props = {
    open: boolean;
    editingIndex: number | null;
    initialItem: PaymentMethodDraft | null;
    disabledPayPalOption?: boolean;
    onClose: () => void;
    onSuccess?: () => void;
};

const AddPaymentMethodModal = ({ open, initialItem, editingIndex, onClose }: Props) => {
    const parentFormik = useFormikContext<InvoiceTemplateFormFields>();

    const initialValues: PaymentMethodDraft = useMemo(() => {
        if (initialItem) return initialItem;

        return {
            paymentMethod: null,
            bankName: '',
            iban: '',
            bic: '',
            url: '',
        };
    }, [initialItem]);

    const modalFormik = useFormik<PaymentMethodDraft>({
        enableReinitialize: true,
        validationSchema: paymentMethodSchema,
        initialValues,
        onSubmit: async (values, helpers) => {
            const errors = await helpers.validateForm();

            if (
                values[PaymentMethodsFormFields.PAYMENT_METHOD] === PaymentMethods.PAY_PAL &&
                payPalExists &&
                !isEditing
            ) {
                Notification({
                    type: 'error',
                    messageTitle: 'PayPal already added',
                    description: 'Only one PayPal payment method can be added.',
                    placement: 'top',
                });
                return;
            }

            if (isPayPal) {
                if (Object.keys(errors).length) {
                    helpers.setTouched({ paymentMethod: true, url: true }, false);
                    return;
                }

                if (!values[PaymentMethodsFormFields.URL]) {
                    Notification({
                        type: 'error',
                        messageTitle: 'Could not add PayPal payment method',
                        description: 'PayPal URL is missing.',
                        placement: 'top',
                    });
                    return;
                }

                parentFormik.setFieldValue('paymentLink', values[PaymentMethodsFormFields.URL] ?? null, true);
            } else {
                if (Object.keys(errors).length) {
                    helpers.setTouched({ paymentMethod: true, bankName: true, iban: true, bic: true }, false);
                    return;
                }

                if (
                    !(
                        values[PaymentMethodsFormFields.BANK_NAME] &&
                        values[PaymentMethodsFormFields.BIC] &&
                        values[PaymentMethodsFormFields.IBAN]
                    )
                ) {
                    Notification({
                        type: 'error',
                        messageTitle: 'Could not add payment method',
                        description: 'Bank details are missing.',
                        placement: 'top',
                    });
                    return;
                }

                const bankAccountValue: InvoiceConfigBankAccount = {
                    bankName: values[PaymentMethodsFormFields.BANK_NAME] ?? '',
                    bic: values[PaymentMethodsFormFields.BIC] ?? '',
                    iban: values[PaymentMethodsFormFields.IBAN] ?? '',
                    paymentMethod: PaymentMethods.BANK_TRANSFER,
                };

                const nextAccounts =
                    editingIndex == null
                        ? [...bankAccounts, bankAccountValue]
                        : bankAccounts.map((ba, i) => (i === editingIndex ? bankAccountValue : ba));

                parentFormik.setFieldValue('bankAccount', nextAccounts, true);
            }

            helpers.resetForm();
            onClose();
        },
    });

    const bankAccounts = parentFormik.values.bankAccount ?? [];

    const isPayPal = modalFormik.values[PaymentMethodsFormFields.PAYMENT_METHOD] === PaymentMethods.PAY_PAL;
    const isBankTransfer = modalFormik.values[PaymentMethodsFormFields.PAYMENT_METHOD] === PaymentMethods.BANK_TRANSFER;
    const isEditingPayPal = initialItem?.[PaymentMethodsFormFields.PAYMENT_METHOD] === PaymentMethods.PAY_PAL;
    const isEditing = editingIndex !== null || isEditingPayPal;
    const payPalExists = !!parentFormik.values.paymentLink;

    const paypalFormElements = (
        <>
            <Row justify="space-between" align="middle">
                <div>
                    <LabelRequiredSymbol style={{ marginRight: 8 }} />
                    <label htmlFor={PaymentMethodsFormFields.URL}>URL:</label>
                </div>
                <FormInput
                    fieldProps={modalFormik.getFieldProps(PaymentMethodsFormFields.URL)}
                    fieldMeta={modalFormik.getFieldMeta(PaymentMethodsFormFields.URL)}
                    fieldHelper={modalFormik.getFieldHelpers(PaymentMethodsFormFields.URL)}
                    placeholder="Enter URL"
                    inputStyles={{ width: 375 }}
                />
            </Row>
        </>
    );

    const bankTransferFormElements = (
        <>
            <Row justify="space-between" align="middle">
                <div>
                    <LabelRequiredSymbol style={{ marginRight: 8 }} />
                    <label htmlFor={PaymentMethodsFormFields.BANK_NAME}>Bank Name:</label>
                </div>
                <FormInput
                    fieldProps={modalFormik.getFieldProps(PaymentMethodsFormFields.BANK_NAME)}
                    fieldMeta={modalFormik.getFieldMeta(PaymentMethodsFormFields.BANK_NAME)}
                    fieldHelper={modalFormik.getFieldHelpers(PaymentMethodsFormFields.BANK_NAME)}
                    placeholder="Enter bank name"
                    inputStyles={{ width: 375 }}
                />
            </Row>

            <Row justify="space-between" align="middle">
                <div>
                    <LabelRequiredSymbol style={{ marginRight: 8 }} />
                    <label htmlFor={PaymentMethodsFormFields.IBAN}>IBAN:</label>
                </div>
                <FormInput
                    fieldProps={modalFormik.getFieldProps(PaymentMethodsFormFields.IBAN)}
                    fieldMeta={modalFormik.getFieldMeta(PaymentMethodsFormFields.IBAN)}
                    fieldHelper={modalFormik.getFieldHelpers(PaymentMethodsFormFields.IBAN)}
                    placeholder="Enter IBAN"
                    inputStyles={{ width: 375 }}
                />
            </Row>

            <Row justify="space-between" align="middle">
                <div>
                    <LabelRequiredSymbol style={{ marginRight: 8 }} />
                    <label htmlFor={PaymentMethodsFormFields.BIC}>BIC:</label>
                </div>
                <FormInput
                    fieldProps={modalFormik.getFieldProps(PaymentMethodsFormFields.BIC)}
                    fieldMeta={modalFormik.getFieldMeta(PaymentMethodsFormFields.BIC)}
                    fieldHelper={modalFormik.getFieldHelpers(PaymentMethodsFormFields.BIC)}
                    placeholder="Enter BIC"
                    inputStyles={{ width: 375 }}
                />
            </Row>
        </>
    );

    return (
        <ConfirmationModal
            open={open}
            title={isEditing ? 'Update Payment Method' : 'Add Payment Method'}
            confirmButtonText={isEditing ? 'Update Payment Method' : 'Add Payment Method'}
            onCancel={() => {
                modalFormik.resetForm();
                onClose();
            }}
            onConfirm={modalFormik.submitForm}
            confirmationButtonDisabled={!modalFormik.isValid || !modalFormik.dirty}
            confirmationButtonLoading={modalFormik.isSubmitting}
            icon={<PlusOutlined />}
            content={
                <Space direction="vertical" size={16} style={{ width: '100%', textAlign: 'left' }}>
                    <Typography.Text>Please select payment method and fill in all required fields.</Typography.Text>

                    <Row justify="space-between" align="middle">
                        <div>
                            <LabelRequiredSymbol style={{ marginRight: 8 }} />
                            <label htmlFor={PaymentMethodsFormFields.PAYMENT_METHOD}>Payment method:</label>
                        </div>
                        <FormSelect
                            // Disable changing payment type on edit
                            disabled={isEditing}
                            hideKeys
                            placeholder="Select Payment Method"
                            fieldProps={modalFormik.getFieldProps(PaymentMethodsFormFields.PAYMENT_METHOD)}
                            fieldHelper={modalFormik.getFieldHelpers(PaymentMethodsFormFields.PAYMENT_METHOD)}
                            fieldMeta={modalFormik.getFieldMeta(PaymentMethodsFormFields.PAYMENT_METHOD)}
                            style={{ minWidth: 375 }}
                            selectOptions={[
                                {
                                    id: PaymentMethods.BANK_TRANSFER,
                                    value: 'Bank Account',
                                    disabled: isEditing,
                                },
                                {
                                    id: PaymentMethods.PAY_PAL,
                                    value: 'PayPal',
                                    disabled: payPalExists,
                                },
                            ]}
                        />
                    </Row>

                    {isPayPal && paypalFormElements}
                    {isBankTransfer && bankTransferFormElements}
                </Space>
            }
        />
    );
};

export default AddPaymentMethodModal;
